/**
 * NOYZ-440 (460, plus tracker row 20): screen-reader fixes over Afterpay's on-site messaging.
 *
 * The "or 4 interest-free payments with Afterpay" line (<square-placement>) and its dialog
 * (<afterpay-modal>) are rendered by Afterpay/Square's own script, inside open shadow roots we
 * don't control. Until Afterpay fixes them (ticket raised with Afterpay), this corrects only what
 * assistive technology hears and the keyboard tab order. Nothing visual or clickable changes:
 *
 *  - Placement: the logo and the (i) icon are two controls that open the same dialog. Keyboard and
 *    screen-reader users get one: the logo, renamed so the sentence still reads "...with Afterpay".
 *    The (i) icon stays visible and clickable with a mouse/touch.
 *  - Dialog: the "Cash App terms" link was announced as "Afterpay terms (New Window)"; every
 *    new-window link is now named after its visible text. The brand logo gets a name; the
 *    decorative banner and step icons are hidden from screen readers.
 *
 * Afterpay rebuilds this markup (the dialog on every open), so the fixes are re-applied from a
 * MutationObserver on each shadow root. If Afterpay changes its markup the selectors simply stop
 * matching and nothing happens.
 */
(function () {
  if (window.afterpayA11y) return;
  window.afterpayA11y = true;

  const TEXT = {
    placementTrigger: 'Afterpay, learn more about paying in 4 installments (opens a dialog)',
    brandLogo: 'Afterpay',
    newWindow: '(opens in a new window)',
  };

  const observed = new WeakSet();

  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function patchPlacement(root) {
    const logo = root.querySelector('button.afterpay-logo');
    const info = root.querySelector('button.afterpay-link');
    if (!logo || !info) return;
    setAttr(logo, 'aria-label', TEXT.placementTrigger);
    setAttr(info, 'tabindex', '-1');
    setAttr(info, 'aria-hidden', 'true');
  }

  function patchModal(root) {
    const dialog = root.querySelector('[role="dialog"]');
    if (!dialog) return;

    dialog.querySelectorAll('svg').forEach((svg) => {
      if (svg.classList.contains('brand-logo')) {
        setAttr(svg, 'role', 'img');
        setAttr(svg, 'aria-label', TEXT.brandLogo);
        return;
      }
      if (svg.closest('[aria-hidden="true"]') || svg.getAttribute('aria-label') || svg.querySelector('title')) return;
      setAttr(svg, 'aria-hidden', 'true');
      setAttr(svg, 'focusable', 'false');
    });

    dialog.querySelectorAll('a[target="_blank"]').forEach((link) => {
      const visibleText = link.textContent.replace(/\s+/g, ' ').trim();
      if (visibleText) setAttr(link, 'aria-label', `${visibleText} ${TEXT.newWindow}`);
    });
  }

  function watch(host) {
    const patch = host.localName === 'square-placement' ? patchPlacement : patchModal;
    const attach = () => {
      const root = host.shadowRoot;
      if (!root) return false;
      if (!observed.has(root)) {
        observed.add(root);
        new MutationObserver(() => patch(root)).observe(root, { childList: true, subtree: true });
      }
      patch(root);
      return true;
    };
    // The shadow root appears once the custom element upgrades.
    if (!attach()) customElements.whenDefined(host.localName).then(() => setTimeout(attach));
  }

  function scan() {
    document.querySelectorAll('square-placement, afterpay-modal').forEach(watch);
  }

  function init() {
    scan();
    // Afterpay inserts the placement next to the price and appends the dialog to <body> later.
    // Only the added node itself is checked, to keep this cheap on a busy product page.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.localName === 'square-placement' || node.localName === 'afterpay-modal') watch(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
